import React from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";

export type FilterChipVariant = "purple" | "blue" | "custom";

interface FilterChipProps {
  children: React.ReactNode;
  variant?: FilterChipVariant;
  icon?: React.ReactNode;
  onRemove?: () => void;
  className?: string;
  customColor?: string; // Hex color for custom variant
  showCheckbox?: boolean;
  checked?: boolean;
}

/**
 * A reusable filter chip component for displaying active filters.
 * Supports different color variants and optional icons/checkboxes.
 * Used for displaying active filters, criteria, tags, etc.
 */
export function FilterChip({
  children,
  variant = "blue",
  icon,
  onRemove,
  className = "",
  customColor,
  showCheckbox = false,
  checked = false,
}: FilterChipProps) {
  // Convert hex to RGB for opacity if custom color is provided
  const getCustomStyles = () => {
    if (variant === "custom" && customColor) {
      const rgb = customColor.replace("#", "");
      const r = parseInt(rgb.substring(0, 2), 16);
      const g = parseInt(rgb.substring(2, 4), 16);
      const b = parseInt(rgb.substring(4, 6), 16);
      return {
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.1)`,
        borderColor: `rgba(${r}, ${g}, ${b}, 0.3)`,
        color: customColor,
      };
    }
    return {};
  };

  const getVariantClasses = () => {
    if (variant === "custom") {
      return "rounded-md text-xs flex items-center gap-1.5 border";
    }
    // One unified token chip for all non-custom variants (016 §5 — replaces the
    // per-type purple/blue chips; `custom` is kept for colour-coded swatches).
    return "px-2 py-1 bg-muted text-foreground border border-border rounded-md text-xs flex items-center gap-1.5";
  };

  const getIconColor = () => {
    if (variant === "custom" && customColor) {
      return { color: customColor };
    }
    return {};
  };

  const getCloseButtonClasses = () => {
    if (variant === "custom") {
      return "ml-1 rounded p-0.5 hover:opacity-70";
    }
    return "ml-1 hover:bg-accent rounded p-0.5";
  };

  const baseClasses = getVariantClasses();
  const customStyles = getCustomStyles();
  const iconColor = getIconColor();
  const closeButtonClasses = getCloseButtonClasses();

  return (
    <div
      className={`${baseClasses} ${className}`}
      style={customStyles}
    >
      {showCheckbox && (
        <div className="flex-shrink-0">
          <input
            type="checkbox"
            checked={checked}
            readOnly
            className="w-3 h-3 rounded border-border text-primary focus:ring-ring"
            style={iconColor}
          />
        </div>
      )}
      {icon && (
        <span className="flex-shrink-0" style={iconColor}>
          {icon}
        </span>
      )}
      <span className="flex-1">{children}</span>
      {onRemove && (
        <button
          onClick={onRemove}
          className={closeButtonClasses}
          style={iconColor}
          aria-label="Remove filter"
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
